import { useState, useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Package, MapPin, CreditCard, ShoppingBag, Loader2 } from "lucide-react";
import { PaymentMethodLabel, Button, Alert, IconButton, PageContent, AlertDialog, Tooltip } from "@printtat/ui";
import { orderApi } from "@/services/orderApi";
import {
  paymentApi,
  type PaymentDetailsResponse,
} from "@/services/paymentApi";
import { ApiError } from "@/lib/apiError";
import { loadPaymobSdk, mountPaymobCheckout, getPaymobRedirectUrl } from "@/lib/paymob";
import { useCartStore } from "@/stores/cartStore";
import { getDefaultMockupPath } from "@/utils/productAssets";
import { queryKeys } from "@/lib/queryKeys";
import {
  formatPrice,
  PAYMENT_RETRY_LIMIT,
  ORDER_STATUS_LABELS,
  CANCELLABLE_STATUSES,
  PAYMENT_STATUS_LABELS,
  type Order,
  type OrderStatus,
  type PaymentStatus,
  type ShippingAddress,
  type StatusHistoryEntry,
} from "@printtat/shared";
import { StatusTimeline } from "./components/StatusTimeline";

const STATUS_COLORS: Record<OrderStatus, string> = {
  pending: "bg-yellow-50 text-yellow-700",
  confirmed: "bg-blue-50 text-blue-700",
  in_production: "bg-purple-50 text-purple-700",
  quality_check: "bg-indigo-50 text-indigo-700",
  shipped: "bg-cyan-50 text-cyan-700",
  out_for_delivery: "bg-teal-50 text-teal-700",
  delivered: "bg-green-50 text-green-700",
  cancelled: "bg-red-50 text-red-700",
  returned: "bg-orange-50 text-orange-700",
};

const PAYMENT_STATUS_COLORS: Record<PaymentStatus, string> = {
  pending: "bg-yellow-50 text-yellow-700",
  awaiting_payment: "bg-amber-50 text-amber-700",
  paid: "bg-green-50 text-green-700",
  failed: "bg-red-50 text-red-700",
  refund_pending: "bg-orange-50 text-orange-700",
  refunded: "bg-gray-50 text-gray-700",
};

const formatDate = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-EG", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Render the Re-order 24h-rate-limit unlock time as both relative ("in 4
 * hours") and absolute ("around 9:10 AM") so a customer skimming the page
 * gets a quick read and one returning later still gets a stable target time.
 * Returns null if the timestamp is missing or already in the past — both
 * cases mean the rate limit has effectively cleared.
 */
const formatReorderUnlock = (
  iso: string | null,
): { relative: string; absolute: string } | null => {
  if (!iso) return null;
  const unlock = new Date(iso);
  const diffMs = unlock.getTime() - Date.now();
  if (diffMs <= 0) return null;

  const hours = Math.round(diffMs / (60 * 60 * 1000));
  const minutes = Math.round(diffMs / (60 * 1000));
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  const relative =
    hours >= 1 ? rtf.format(hours, "hour") : rtf.format(minutes, "minute");

  const absolute = unlock.toLocaleString("en-EG", {
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
  });

  return { relative, absolute };
};

const AddressCard = ({
  title,
  address,
}: {
  title: string;
  address: ShippingAddress;
}) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5">
    <div className="flex items-center gap-2 mb-3">
      <MapPin className="w-4 h-4 text-gray-700" />
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    </div>
    <div className="text-sm text-gray-600 space-y-1">
      <p className="font-medium text-gray-900">{address.fullName}</p>
      <p>{address.phone}</p>
      <p>{address.addressLine1}</p>
      {address.addressLine2 && <p>{address.addressLine2}</p>}
      <p>
        {address.city}, {address.governorate}
        {address.postalCode ? ` ${address.postalCode}` : ""}
      </p>
    </div>
  </div>
);

const Skeleton = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse bg-gray-200 rounded-md ${className}`} />
);

const OrderDetailSkeleton = () => (
  <PageContent>
    {/* Header */}
    <div className="flex items-center justify-between mb-8">
      <div className="flex items-start gap-3">
        <Skeleton className="w-8 h-8 rounded-lg" />
        <div>
          <div className="flex items-center gap-2">
            <Skeleton className="w-48 h-6" />
            <Skeleton className="w-16 h-5 rounded-full" />
          </div>
          <Skeleton className="w-40 h-4 mt-1.5" />
        </div>
      </div>
      <Skeleton className="w-28 h-9 rounded-lg" />
    </div>

    <div className="flex flex-col lg:flex-row gap-6">
      {/* Items */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 flex-1">
        <div className="flex items-center gap-2 mb-5">
          <Skeleton className="w-4 h-4" />
          <Skeleton className="w-20 h-4" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex gap-4 pb-4 border-b border-gray-100 last:border-0 last:pb-0">
              <Skeleton className="w-16 h-16 rounded-lg" />
              <div className="flex-1">
                <Skeleton className="w-32 h-4 mb-1.5" />
                <Skeleton className="w-24 h-3 mb-1" />
                <Skeleton className="w-28 h-3" />
              </div>
              <div className="text-right">
                <Skeleton className="w-16 h-4 mb-1 ml-auto" />
                <Skeleton className="w-20 h-3 ml-auto" />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Status Timeline */}
      <div className="flex-1 max-w-[220px]">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <Skeleton className="w-28 h-4 mb-4" />
          <div className="space-y-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="w-5 h-5 rounded-full" />
                <Skeleton className="w-24 h-3" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right column */}
      <div className="w-full lg:w-[320px] flex-shrink-0 space-y-6">
        {/* Order Summary */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <Skeleton className="w-28 h-4 mb-4" />
          <div className="space-y-2">
            <div className="flex justify-between">
              <Skeleton className="w-16 h-4" />
              <Skeleton className="w-20 h-4" />
            </div>
            <div className="flex justify-between">
              <Skeleton className="w-16 h-4" />
              <Skeleton className="w-12 h-4" />
            </div>
            <div className="flex justify-between pt-2 border-t border-gray-100">
              <Skeleton className="w-12 h-5" />
              <Skeleton className="w-20 h-5" />
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center gap-2">
            <Skeleton className="w-4 h-4" />
            <Skeleton className="w-28 h-4" />
          </div>
        </div>

        {/* Shipping Address */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Skeleton className="w-4 h-4" />
            <Skeleton className="w-28 h-4" />
          </div>
          <div className="space-y-1.5">
            <Skeleton className="w-24 h-4" />
            <Skeleton className="w-28 h-3" />
            <Skeleton className="w-36 h-3" />
            <Skeleton className="w-32 h-3" />
          </div>
        </div>
      </div>
    </div>
  </PageContent>
);

export const OrderDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  // Complete Payment state
  const [isInitiatingPayment, setIsInitiatingPayment] = useState(false);
  const [showPaymentOverlay, setShowPaymentOverlay] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Re-order state
  const [isReordering, setIsReordering] = useState(false);
  const [reorderError, setReorderError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  const handleCompletePayment = async () => {
    if (!id) return;
    setIsInitiatingPayment(true);
    setPaymentError(null);

    try {
      const result = await paymentApi.initiate(id);

      // Pre-initiate reconciliation found a successful Paymob transaction
      // we missed (webhook dropped). Order is already paid — just refresh
      // the page state, no checkout needed.
      if ('alreadyPaid' in result) {
        setIsInitiatingPayment(false);
        useCartStore.setState({ items: [] });
        await queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(id) });
        return;
      }

      const { clientSecret } = result;
      setShowPaymentOverlay(true);
      setIsInitiatingPayment(false);

      try {
        await loadPaymobSdk();
        requestAnimationFrame(() => {
          try {
            mountPaymobCheckout(clientSecret, '#paymob-checkout-detail');

            // Start polling
            const poll = async () => {
              try {
                const status = await paymentApi.getStatus(id);
                if (status.paymentStatus === 'paid') {
                  if (pollTimerRef.current) clearInterval(pollTimerRef.current);
                  if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
                  setShowPaymentOverlay(false);
                  useCartStore.setState({ items: [] });
                  queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(id) });
                } else if (status.paymentStatus === 'failed') {
                  if (pollTimerRef.current) clearInterval(pollTimerRef.current);
                  if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
                  setShowPaymentOverlay(false);
                  setPaymentError('Payment was not successful.');
                  queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(id) });
                }
              } catch {
                // Continue polling
              }
            };

            pollTimerRef.current = setInterval(poll, 3000);
            poll();
            pollTimeoutRef.current = setTimeout(() => {
              if (pollTimerRef.current) clearInterval(pollTimerRef.current);
              setShowPaymentOverlay(false);
              queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(id) });
            }, 10 * 60 * 1000);
          } catch {
            setShowPaymentOverlay(false);
            window.location.href = getPaymobRedirectUrl(clientSecret);
          }
        });
      } catch {
        setShowPaymentOverlay(false);
        window.location.href = getPaymobRedirectUrl(clientSecret);
      }
    } catch (error) {
      setIsInitiatingPayment(false);
      setPaymentError(
        error instanceof Error ? error.message : 'Failed to initiate payment',
      );
    }
  };

  const {
    data: order,
    isLoading,
    error,
  } = useQuery<Order>({
    queryKey: queryKeys.orders.detail(id!),
    queryFn: () => orderApi.getOrder(id!),
    enabled: !!id,
  });

  const { data: paymentDetails } = useQuery<PaymentDetailsResponse | null>({
    queryKey: [...queryKeys.orders.detail(id!), 'payment'],
    queryFn: () => paymentApi.getDetails(id!),
    enabled: !!id && order?.paymentMethod === 'online',
  });

  const handleCancel = async () => {
    if (!id) return;
    setIsCancelling(true);
    setCancelError(null);
    try {
      await orderApi.cancelOrder(id);
      await queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(id!) });
      setShowCancelConfirm(false);
    } catch (err) {
      setCancelError(
        err instanceof Error ? err.message : "Failed to cancel order",
      );
    } finally {
      setIsCancelling(false);
    }
  };

  const handleReorder = async () => {
    if (!id) return;
    setIsReordering(true);
    setReorderError(null);
    try {
      const { orderId: newOrderId } = await orderApi.reorder(id);
      navigate(`/orders/${newOrderId}`);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'PREVIOUS_PAYMENT_SUCCEEDED') {
        // Race: a delayed Paymob webhook landed during our reconcile.
        // Refresh the order — its payment_status will show `paid` now.
        await queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(id) });
        setReorderError(
          'Your previous payment actually went through. Refreshing the order…',
        );
      } else if (err instanceof ApiError && err.code === 'REORDER_LIMIT_REACHED') {
        setReorderError('Re-order limit reached. Please contact support.');
      } else {
        setReorderError(
          err instanceof Error ? err.message : 'Failed to re-order',
        );
      }
    } finally {
      setIsReordering(false);
    }
  };

  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const handleImgError = useCallback((itemId: string) => {
    setFailedImages((prev) => new Set(prev).add(itemId));
  }, []);

  if (isLoading) {
    return <OrderDetailSkeleton />;
  }

  if (error || !order) {
    return (
      <PageContent>
        <Alert variant="error">
          {error instanceof Error ? error.message : "Order not found"}
        </Alert>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate("/orders")}
        >
          Back to Orders
        </Button>
      </PageContent>
    );
  }

  const canCancel = CANCELLABLE_STATUSES.includes(order.status as OrderStatus);
  const statusColor =
    STATUS_COLORS[order.status as OrderStatus] || "bg-gray-50 text-gray-700";

  const attemptCount = paymentDetails?.attemptCount ?? 0;
  const isOnlineUnpaid =
    (order.paymentStatus === "awaiting_payment" ||
      order.paymentStatus === "failed") &&
    order.paymentMethod === "online";
  const showReorderInsteadOfRetry =
    isOnlineUnpaid && attemptCount >= PAYMENT_RETRY_LIMIT;
  const reorderUnavailable = !order.reorderAvailability.available;
  const reorderUnlockText = formatReorderUnlock(
    order.reorderAvailability.nextAvailableAt,
  );

  return (
    <PageContent>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-start gap-3">
          <IconButton
            size="sm"
            onClick={() => navigate("/orders")}
          >
            <ArrowLeft className="w-4 h-4 text-gray-600" />
          </IconButton>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-gray-900">
                {order.orderNumber}
              </h1>
              <span
                className={`text-xs font-medium px-2.5 py-0.5 rounded-full ${statusColor}`}
              >
                {ORDER_STATUS_LABELS[order.status as OrderStatus] ||
                  order.status}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              Placed on {formatDate(order.createdAt)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isOnlineUnpaid && !showReorderInsteadOfRetry && (
            <Button
              loading={isInitiatingPayment}
              onClick={handleCompletePayment}
            >
              <CreditCard className="w-4 h-4 mr-1.5" />
              {order.paymentStatus === "failed" ? "Retry Payment" : "Complete Payment"}
            </Button>
          )}
          {showReorderInsteadOfRetry && reorderUnavailable && (
            <Tooltip
              content={
                reorderUnlockText
                  ? `Daily Re-order limit reached. Available again ${reorderUnlockText.relative} (${reorderUnlockText.absolute}). Contact support if you need help sooner.`
                  : "Daily Re-order limit reached. Contact support for help."
              }
            >
              <span tabIndex={0}>
                <Button disabled>
                  <ShoppingBag className="w-4 h-4 mr-1.5" />
                  Re-order
                </Button>
              </span>
            </Tooltip>
          )}
          {showReorderInsteadOfRetry && !reorderUnavailable && (
            <Button loading={isReordering} onClick={handleReorder}>
              <ShoppingBag className="w-4 h-4 mr-1.5" />
              Re-order
            </Button>
          )}
          {canCancel && !showReorderInsteadOfRetry && (
            <Button
              variant="outline"
              onClick={() => setShowCancelConfirm(true)}
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              Cancel Order
            </Button>
          )}
        </div>
      </div>

      {showReorderInsteadOfRetry && !reorderUnavailable && (
        <Alert variant="info" className="mb-6">
          You've tried this payment {PAYMENT_RETRY_LIMIT} times. Start a fresh order to try again, or contact support if you'd rather cancel this one.
        </Alert>
      )}
      {showReorderInsteadOfRetry && reorderUnavailable && (
        <Alert variant="warning" className="mb-6">
          You've used your daily Re-order limit (3 in 24 hours).{" "}
          {reorderUnlockText
            ? `Re-order will be available again ${reorderUnlockText.relative} (around ${reorderUnlockText.absolute}).`
            : "Please try again later."}{" "}
          Contact support if you need help sooner.
        </Alert>
      )}

      {reorderError && (
        <Alert variant="error" className="mb-6">
          {reorderError}
        </Alert>
      )}

      {cancelError && (
        <Alert variant="error" className="mb-6">
          {cancelError}
        </Alert>
      )}

      {paymentError && (
        <Alert variant="error" className="mb-6">
          {paymentError}
        </Alert>
      )}

      <div className="flex flex-col lg:flex-row gap-6 flex-1">
        {/* Order Items */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 flex-1">
          <div className="flex items-center gap-2 mb-5">
            <Package className="w-4 h-4 text-gray-700" />
            <h3 className="text-sm font-semibold text-gray-900">
              Items ({order.items.length})
            </h3>
          </div>

          <div className="space-y-4">
            {order.items.map((item) => (
              <div
                key={item.id}
                className="flex gap-4 pb-4 border-b border-gray-100 last:border-0 last:pb-0"
              >
                <div className="w-16 h-16 rounded-lg bg-gray-100 border border-gray-200 overflow-hidden flex-shrink-0">
                  {!failedImages.has(item.id) && (item.previewUrl || item.productSlug) ? (
                    <img
                      src={item.previewUrl ?? getDefaultMockupPath(item.productSlug!, "front")}
                      alt={item.designName}
                      className="w-full h-full object-cover"
                      onError={() => handleImgError(item.id)}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ShoppingBag className="w-5 h-5 text-gray-300" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900">
                    {item.designName}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {item.productName}
                  </p>
                  <p className="text-xs text-gray-500">
                    {item.variantLabel} &times; {item.quantity}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-medium text-gray-900">
                    {formatPrice(item.unitPrice * item.quantity)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatPrice(item.unitPrice)} / unit
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Left column */}
        <div className="flex-1 space-y-6 max-w-[220px]">
          {/* Status Timeline */}
          <StatusTimeline
            currentStatus={order.status as OrderStatus}
            statusHistory={order.statusHistory as StatusHistoryEntry[]}
          />
        </div>

        {/* Right column */}
        <div className="w-full lg:w-[320px] flex-shrink-0 space-y-6">
          {/* Order Summary */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">
              Order Summary
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Subtotal</span>
                <span className="text-gray-900">
                  {formatPrice(order.subtotal)}
                </span>
              </div>
              {order.discountAmount > 0 && (
                <div className="flex justify-between">
                  <span className="text-green-600">
                    Discount
                    {order.discountDetails
                      ? ` (${order.discountDetails.code})`
                      : ""}
                  </span>
                  <span className="text-green-600">
                    −{formatPrice(order.discountAmount)}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">Shipping</span>
                <span className="text-gray-900">
                  {order.shippingCost > 0
                    ? formatPrice(order.shippingCost)
                    : "Free"}
                </span>
              </div>
              <div className="flex justify-between font-semibold pt-2 border-t border-gray-100">
                <span className="text-gray-900">Total</span>
                <span className="text-gray-900">
                  {formatPrice(order.totalAmount)}
                </span>
              </div>
            </div>

          </div>

          {/* Payment */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-gray-700" />
                <h3 className="text-sm font-semibold text-gray-900">Payment</h3>
              </div>
              <span
                className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  PAYMENT_STATUS_COLORS[order.paymentStatus as PaymentStatus] || "bg-gray-50 text-gray-700"
                }`}
              >
                {PAYMENT_STATUS_LABELS[order.paymentStatus as PaymentStatus] || order.paymentStatus}
              </span>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Method</span>
                <span className="text-gray-900">
                  <PaymentMethodLabel
                    paymentMethod={order.paymentMethod}
                    paymentMethodDetail={paymentDetails?.paymentMethod}
                    cardBrand={paymentDetails?.cardBrand}
                    cardLastFour={paymentDetails?.cardLastFour}
                    iconSize="w-4 h-4"
                  />
                </span>
              </div>

              {paymentDetails?.transactionId && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Transaction</span>
                  <span className="text-gray-900 font-mono text-xs">
                    {paymentDetails.transactionId}
                  </span>
                </div>
              )}

              {paymentDetails?.intentionId && !paymentDetails.transactionId && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Intention</span>
                  <span className="text-gray-900 font-mono text-xs">
                    {paymentDetails.intentionId}
                  </span>
                </div>
              )}

              {paymentDetails?.paidAt && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Paid at</span>
                  <span className="text-gray-900 text-xs">
                    {formatDate(paymentDetails.paidAt)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Shipping Address */}
          <AddressCard
            title="Shipping Address"
            address={order.shippingAddress as ShippingAddress}
          />

          {/* Billing Address */}
          {order.billingAddress && (
            <AddressCard
              title="Billing Address"
              address={order.billingAddress as ShippingAddress}
            />
          )}

          {/* Notes */}
          {order.notes && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                Order Notes
              </h3>
              <p className="text-sm text-gray-600">{order.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* Payment Overlay */}
      {showPaymentOverlay && (
        <div className="fixed inset-0 z-50 bg-white/95 flex items-center justify-center">
          <div className="w-full max-w-lg mx-auto px-6 text-center space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-1">
                Complete Your Payment
              </h2>
              <p className="text-sm text-gray-500">
                Secure payment powered by Paymob
              </p>
            </div>
            <div id="paymob-checkout-detail" className="min-h-15" />
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Waiting for payment confirmation...</span>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Confirmation */}
      <AlertDialog
        open={showCancelConfirm}
        onOpenChange={(open) => !open && setShowCancelConfirm(false)}
        title="Cancel Order"
        description={`Are you sure you want to cancel order ${order.orderNumber}? This action cannot be undone.`}
        confirmText="Cancel Order"
        onConfirm={handleCancel}
        variant="destructive"
        loading={isCancelling}
      />
    </PageContent>
  );
};
